import { useEffect, useRef, useState } from 'react'
import {
  defaultOrgNameFromUserName,
  defaultUserNameFromEmail,
  finishEnroll,
  requestCode,
  verifyCode,
} from './flow'
import { useAppStore } from '../../store/app'
import { StarIcon } from '../../components/icons'
import './RegistrationScreen.css'

type Step = 'email' | 'code' | 'names'

export function RegistrationScreen() {
  // There is no invite step. Invitations are looked up server-side from
  // the verified email, so an invitee signs in exactly like everyone else
  // and finds the offer waiting on the other side.
  //
  // An invitation link may carry the invited address as a prefill hint —
  // it names the mailbox but authorizes nothing (the OTP still proves
  // ownership). Seed the email field from it and consume it so a later
  // manual edit sticks.
  const invitePrefillEmail = useAppStore((s) => s.invitePrefillEmail)
  const setInvitePrefillEmail = useAppStore((s) => s.setInvitePrefillEmail)
  // Set when this session ended because the server revoked this device
  // (remote unenroll), so we can explain why they're back at the login
  // screen instead of silently dropping them here. Cleared on re-register.
  const sessionEndedReason = useAppStore((s) => s.sessionEndedReason)

  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState(() => invitePrefillEmail ?? '')
  const [code, setCode] = useState('')
  const [hasPendingInvites, setHasPendingInvites] = useState(false)
  const [sessionToken, setSessionToken] = useState<string | null>(null)
  const [userName, setUserName] = useState('')
  const [orgName, setOrgName] = useState('')
  // Org name auto-tracks the name field ("Matt" → "Matt's Household")
  // until the user edits it directly. Manual edits stick.
  const [orgNameTouched, setOrgNameTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const emailRef = useRef<HTMLInputElement>(null)
  const codeRef = useRef<HTMLInputElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  // On native cold launch the universal-link bridge event fires after the
  // WebView mounts, so the prefill can arrive late. Seed the field then
  // too — but only while still on the email step and only if the user
  // hasn't already typed something, so we never clobber their edit. Clear
  // it either way so it's a one-shot.
  useEffect(() => {
    if (!invitePrefillEmail) return
    if (step === 'email' && email.trim() === '') setEmail(invitePrefillEmail)
    setInvitePrefillEmail(null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitePrefillEmail])

  useEffect(() => {
    const t = setTimeout(() => emailRef.current?.focus(), 200)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    if (step === 'code') {
      const t = setTimeout(() => codeRef.current?.focus(), 200)
      return () => clearTimeout(t)
    }
    if (step === 'names') {
      const t = setTimeout(() => nameRef.current?.focus(), 200)
      return () => clearTimeout(t)
    }
  }, [step])

  async function submitEmail() {
    const trimmed = email.trim()
    if (!trimmed || submitting) return
    // Focus the code input synchronously inside the user-gesture stack so
    // iOS opens the keyboard. A focus() that happens after `await` (or
    // inside a setTimeout) is no longer treated as user-initiated and
    // iOS silently drops the keyboard request. The code input is always
    // mounted for this reason — see the `display:none` block below.
    codeRef.current?.focus()
    setError(null)
    setSubmitting(true)
    try {
      await requestCode(trimmed)
      setStep('code')
    } catch (err) {
      setError(errMsg(err))
    } finally {
      setSubmitting(false)
    }
  }

  async function submitCode() {
    if (code.length !== 6 || submitting) return
    setError(null)
    setSubmitting(true)
    try {
      const { sessionToken: token, isNewUser, hasPendingInvites: invited } = await verifyCode({
        email,
        code,
      })
      setSessionToken(token)
      setHasPendingInvites(invited)
      if (isNewUser) {
        // Pre-fill the name fields so the user can Enter-through with
        // the suggested defaults. Org name tracks the user-name field
        // (until the user edits it) — see onUserNameChange below.
        const suggestedUser = defaultUserNameFromEmail(email)
        setUserName(suggestedUser)
        setOrgName(defaultOrgNameFromUserName(suggestedUser))
        setOrgNameTouched(false)
        setStep('names')
        setSubmitting(false)
        return
      }
      // Returning user — go straight to enroll with no names supplied.
      await finishEnroll({ email, sessionToken: token })
    } catch (err) {
      setError(errMsg(err))
      setCode('')
    } finally {
      // finishEnroll path already un-sets submitting via the store flip
      // unmounting the screen; the happy path never reaches this line.
      // The names-step path sets it false above before returning. The
      // only case we hit here is errors.
      setSubmitting(false)
    }
  }

  async function submitNames() {
    if (!sessionToken || submitting) return
    setError(null)
    setSubmitting(true)
    try {
      await finishEnroll({
        email,
        sessionToken,
        userName,
        // Invitees join an existing household — the server skips
        // bootstrap-org creation for them, so an org name would be
        // collected and then dropped.
        organizationName: hasPendingInvites ? undefined : orgName,
      })
    } catch (err) {
      setError(errMsg(err))
      setSubmitting(false)
    }
  }

  function onCodeChange(value: string) {
    const digits = value.replace(/\D/g, '').slice(0, 6)
    setCode(digits)
    if (digits.length === 6 && !submitting) {
      queueMicrotask(() => {
        if (digits.length === 6) void submitCode()
      })
    }
  }

  function backToEmail() {
    if (submitting) return
    setCode('')
    setSessionToken(null)
    setUserName('')
    setOrgName('')
    setOrgNameTouched(false)
    setHasPendingInvites(false)
    setError(null)
    setStep('email')
  }

  function onUserNameChange(value: string) {
    setUserName(value)
    if (!orgNameTouched) {
      setOrgName(defaultOrgNameFromUserName(value))
    }
  }

  function onOrgNameChange(value: string) {
    setOrgName(value)
    setOrgNameTouched(true)
  }

  const subtitle =
    step === 'email'
      ? "Enter your email and we'll send you a code."
      : step === 'code'
        ? 'Enter the 6-digit code we just sent.'
        : 'One last step: how should we label your account?'

  return (
    <div className="registration">
      <div className="registration__spacer" />
      <div className="registration__hero">
        <div className="registration__sparkles" aria-hidden>
          <StarIcon className="registration__star-icon" />
        </div>
        <h1 className="registration__title">Welcome to Hearth</h1>
        <p className="registration__subtitle">{subtitle}</p>
      </div>

      {step === 'email' && sessionEndedReason === 'revoked' && (
        <div className="registration__notice" role="status">
          This device was signed out because its access was removed. Sign in
          again to reconnect.
        </div>
      )}

      {step === 'email' && (
        <form
          className="registration__form"
          onSubmit={(e) => {
            e.preventDefault()
            void submitEmail()
          }}
        >
          <input
            ref={emailRef}
            type="email"
            inputMode="email"
            enterKeyHint="send"
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="registration__input"
          />
          <button
            type="submit"
            className="registration__submit"
            disabled={submitting || email.trim() === ''}
          >
            {submitting ? 'Sending…' : 'Send code'}
          </button>

          <p className="registration__invite-hint">
            Invited to a household? Sign in with the email address the
            invitation was sent to — it&apos;ll be waiting for you.
          </p>
        </form>
      )}

      {/* Always mounted and laid out so submitEmail's synchronous
          codeRef.focus() inside the user gesture works on iOS. iOS
          ignores focus() on display:none / visibility:hidden inputs and
          won't open the keyboard, so we hide via opacity + offscreen
          positioning instead. The form is moved into normal flow once
          step === 'code'. */}
      <div
        className="registration__form"
        style={
          step === 'code'
            ? undefined
            : {
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                opacity: 0,
                pointerEvents: 'none',
              }
        }
        aria-hidden={step !== 'code'}
      >
        <input
            ref={codeRef}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            value={code}
            onChange={(e) => onCodeChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submitCode()
            }}
            className="registration__input registration__input--code"
            maxLength={6}
          />
          <button
            type="button"
            className="registration__submit"
            disabled={submitting || code.length !== 6}
            onClick={submitCode}
          >
            {submitting ? 'Verifying…' : 'Verify'}
          </button>
          <button
            type="button"
            className="registration__back"
            disabled={submitting}
            onClick={backToEmail}
          >
            Use a different email
          </button>
        </div>

      {step === 'names' && (
        <div className="registration__form">
          <label className="registration__label">Your name</label>
          <input
            ref={nameRef}
            type="text"
            autoCapitalize="words"
            autoComplete="name"
            value={userName}
            onChange={(e) => onUserNameChange(e.target.value)}
            onKeyDown={(e) => {
              // Without the org-name field below to soak up Enter,
              // submit directly from the name input on the invite path.
              if (e.key === 'Enter' && hasPendingInvites) void submitNames()
            }}
            className="registration__input"
          />
          {hasPendingInvites && (
            <p className="registration__hint">
              Shown to other household members and your agents on messages and
              approvals.
            </p>
          )}
          {!hasPendingInvites && (
            <>
              <label className="registration__label">Household name</label>
              <input
                type="text"
                autoCapitalize="words"
                value={orgName}
                onChange={(e) => onOrgNameChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void submitNames()
                }}
                className="registration__input"
              />
            </>
          )}
          <button
            type="button"
            className="registration__submit"
            disabled={submitting || userName.trim() === ''}
            onClick={submitNames}
          >
            {submitting ? 'Finishing…' : 'Continue'}
          </button>
        </div>
      )}

      {error && <div className="registration__error">{error}</div>}

      <div className="registration__spacer registration__spacer--bottom" />
    </div>
  )
}

function errMsg(err: unknown): string {
  if (err instanceof Error) return err.message
  return String(err)
}
